import React, { useEffect, useState } from 'react';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { WithSingleSelect } from '@components/design';
import { useSelector } from 'react-redux';
import { buildOptions } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { FeatureOption, FilterKeys, Filters, getPageTypeWithPageName, Option, PageType } from '@utils/pageConfig/Types';
import { NO_OPTIONS } from '@utils/StringConstants';
import { multiCountryDisabledFilters } from '@utils/multiCountryUtils/MultiCountryUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { isAllowSearchBox } from '@utils/FilterUtils';

export interface FeatureSelectorProps {
  isDisabled: boolean;
  label: string;
  featureValues: Filters[];
  featureOptions: (option: FeatureOption) => void;
  selectedOption: Option;
  chartName: string;
  id?: string;
  className?: string;
  withoutDiv?: boolean;
  featureOnChange?: (option: FeatureOption) => void; // handle on change event and it's optional for now
}

const FeatureSelector: React.FC<FeatureSelectorProps> = ({
  isDisabled,
  label,
  featureValues,
  featureOptions,
  selectedOption,
  chartName,
  id,
  withoutDiv,
  featureOnChange,
}) => {
  const [featureValue, setFeatureValue] = useState<string | null>(selectedOption?.value);
  const options = buildOptions(featureValues);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  const countryOptions =
    sideFiltersState?.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters[FilterKeys.COUNTRY]?.options;

  const filteredOptions =
    countryOptions?.length === 1 ? options : options.filter((x) => !multiCountryDisabledFilters.includes(x.filterKey));

  const handleChartInteractionTracking = (optionChosen: string) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: label as ChartElementName,
      elementType: ChartElementType.FILTER,
      optionChosen,
      chartName,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };

  // Default with div for main filters. No div for other uses
  const divWrapper = (item: JSX.Element): JSX.Element => {
    return !withoutDiv ? <div>{item}</div> : item;
  };

  const defaultId = 'show-within-selector';
  const idWrapper = id ?? defaultId;

  useEffect(() => {
    const option = filteredOptions.find((o) => o.value === featureValue);
    if (option && option?.value !== selectedOption?.value) {
      handleChartInteractionTracking(option.label);
      featureOptions(option);

      if (featureOnChange) {
        featureOnChange(option);
      }
    }
  }, [featureValue]);

  const value = () => {
    const hasFeatureValue = featureValue !== '';
    const hasNoFilteredOptions = filteredOptions.length === 0;

    if (hasFeatureValue) {
      return featureValue;
    } else if (hasNoFilteredOptions) {
      return '';
    } else {
      return filteredOptions[0].value;
    }
  };

  return divWrapper(
    <div
      key={`${filteredOptions}-${featureValue}`} //NOSONAR typescript:S6551
    >
      <WithSingleSelect
        id={idWrapper}
        name={idWrapper}
        label={label}
        placeholder={NO_OPTIONS}
        disabled={isDisabled}
        options={
          filteredOptions.length
            ? filteredOptions
            : [
                {
                  dataType: NO_OPTIONS,
                  label: NO_OPTIONS,
                  feature: NO_OPTIONS,
                  value: NO_OPTIONS,
                  filterKey: NO_OPTIONS,
                },
              ]
        }
        value={value()}
        onChange={(option) => {
          setFeatureValue(option);
        }}
        combobox={isAllowSearchBox(filteredOptions)}
      />
    </div>,
  );
};

export default FeatureSelector;
